'use client';

import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type FormEvent, type ReactNode, useState } from 'react';
import { InitialsBadge } from '../DataTableCells';
import { GlassIconButton } from '../GlassIconButton';
import { GlassTextField } from '../GlassTextField';

export interface CommentThreadAuthor {
  id: string;
  name: string;
  initials?: string;
  avatarUrl?: string;
  /** Optional role/title rendered next to the name in the header. */
  role?: ReactNode;
}

export interface CommentThreadEntry {
  id: string;
  author: CommentThreadAuthor;
  body: ReactNode;
  /** ISO datetime — rendered as a localized timestamp in the header. */
  createdAt?: string;
  /** Optional accent flag (e.g. Astor responses can be tinted differently). */
  variant?: 'default' | 'astor';
}

export interface CommentThreadProps {
  /** Existing comments (chronological order, oldest first). */
  comments: CommentThreadEntry[];
  /** Placeholder for the composer text field. Default "Add a note…". */
  placeholder?: string;
  /** Submit handler — receives trimmed text. Returns a Promise to await before clearing. */
  onSubmit?: (text: string) => void | Promise<void>;
  /** Optional header title (e.g. "Reviewer notes"). */
  title?: ReactNode;
  /** Optional empty-state message when comments.length === 0. */
  emptyMessage?: ReactNode;
  /** Hide the composer (read-only mode). Default false. */
  readOnly?: boolean;
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

function formatTimestamp(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function CommentThread({
  comments,
  placeholder = 'Add a note…',
  onSubmit,
  title,
  emptyMessage = 'No notes yet.',
  readOnly = false,
  sx,
}: CommentThreadProps) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !onSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={sx}>
      {title && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1,
          }}
        >
          {title}
        </Typography>
      )}

      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {comments.length === 0 ? (
          <Box
            component="li"
            sx={{
              py: 1.5,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          comments.map((comment) => {
            const initials =
              comment.author.initials ??
              comment.author.name
                .split(/\s+/)
                .map((w) => w[0])
                .filter(Boolean)
                .slice(0, 2)
                .join('');
            const timestamp = formatTimestamp(comment.createdAt);

            return (
              <Box key={comment.id} component="li" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                {comment.author.avatarUrl ? (
                  <Box
                    component="img"
                    src={comment.author.avatarUrl}
                    alt=""
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  />
                ) : (
                  <InitialsBadge
                    initials={initials || '?'}
                    size="md"
                    colorSeed={comment.author.id}
                    sx={{ flexShrink: 0, mt: 0.25 }}
                  />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                      }}
                    >
                      {comment.author.name}
                    </Typography>
                    {comment.author.role && (
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                        {comment.author.role}
                      </Typography>
                    )}
                    {timestamp && (
                      <Typography
                        component="time"
                        variant="caption"
                        sx={{ color: 'text.disabled', ml: 'auto' }}
                        dateTime={comment.createdAt}
                      >
                        {timestamp}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.55,
                      mt: 0.25,
                    }}
                  >
                    {comment.body}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {!readOnly && onSubmit && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            mt: 1.5,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          <GlassTextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            size="small"
            disabled={submitting}
          />
          <GlassIconButton
            type="submit"
            aria-label="Post note"
            disabled={submitting || text.trim().length === 0}
            size="medium"
            color="primary"
          >
            <SendIcon fontSize="small" />
          </GlassIconButton>
        </Box>
      )}
    </Box>
  );
}

CommentThread.displayName = 'CommentThread';

export default CommentThread;
